import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  processDificultad,
  processTieneImagen,
  validateBulkUploadData,
  ValidationError,
} from "../../../utils/validation";
import { BulkUploadValidation } from "./BulkUploadValidation";

interface BulkUploadProps {
  onUpload: (data: any[]) => void;
  onDownloadTemplate: () => void;
  loading?: boolean;
  title: string;
  description: string;
}

export const BulkUpload: React.FC<BulkUploadProps> = ({
  onUpload,
  onDownloadTemplate,
  loading = false,
  title,
  description,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processedData, setProcessedData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    []
  );
  const [showValidation, setShowValidation] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
      ) {
        setSelectedFile(file);
        processFile(file);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      processFile(file);
    }
  };

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // Validar que tenga las columnas correctas
        if (jsonData.length < 2) {
          alert(
            "El archivo debe tener al menos una fila de datos además del encabezado"
          );
          return;
        }

        const headers = jsonData[0] as string[];
        const expectedHeaders = [
          "Año",
          "Unidad",
          "Tema",
          "Objetivos de Aprendizaje",
          "Explicación",
          "Ejemplo",
          "Resultado",
          "Dificultad",
          "Conceptos Previos",
          "Tiene Imagen",
        ];

        const missingHeaders = expectedHeaders.filter(
          (header) =>
            !headers.some((h) =>
              h?.toString().toLowerCase().includes(header.toLowerCase())
            )
        );

        if (missingHeaders.length > 0) {
          alert(
            `El archivo debe contener las columnas: ${expectedHeaders.join(
              ", "
            )}`
          );
          return;
        }

        // Convertir datos a formato esperado
        const rawData = jsonData
          .slice(1)
          .map((row: any) => {
            // Crear un objeto con los datos de la fila usando los headers
            const rowData: any = {};
            headers.forEach((header, index) => {
              rowData[header] = row[index];
            });

            return {
              anio: rowData["Año"]?.toString().trim(),
              unidad: rowData["Unidad"]?.toString().trim(),
              tema: rowData["Tema"]?.toString().trim(),
              objetivosAprendizaje:
                rowData["Objetivos de Aprendizaje"]?.toString().trim() || "",
              explicacion: rowData["Explicación"]?.toString().trim() || "",
              ejemplo: rowData["Ejemplo"]?.toString().trim() || "",
              resultado: rowData["Resultado"]?.toString().trim() || "",
              dificultad: rowData["Dificultad"] || 1,
              conceptosPrevios:
                rowData["Conceptos Previos"]?.toString().trim() || "",
              tieneImagen: rowData["Tiene Imagen"] || false,
            };
          })
          .filter((item) => item.anio && item.unidad && item.tema);

        // Validar datos
        const errors = validateBulkUploadData(rawData);
        setValidationErrors(errors);
        setProcessedData(rawData);
        setShowValidation(true);
      } catch (error) {
        console.error("Error procesando archivo:", error);
        alert(
          "Error al procesar el archivo. Asegúrate de que sea un archivo Excel válido."
        );
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleConfirmUpload = async () => {
    setUploadLoading(true);
    try {
      // Procesar datos para el backend
      const finalData = processedData.map((row: any) => ({
        ...row,
        dificultad: processDificultad(row.dificultad),
        tieneImagen: processTieneImagen(row.tieneImagen),
      }));

      await onUpload(finalData);

      // Limpiar estado después de carga exitosa
      setShowValidation(false);
      setProcessedData([]);
      setValidationErrors([]);
      setSelectedFile(null);
    } catch (error) {
      console.error("Error en la carga:", error);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleCancelUpload = () => {
    setShowValidation(false);
    setProcessedData([]);
    setValidationErrors([]);
    setSelectedFile(null);
  };

  // Si estamos mostrando la validación, renderizar el componente de validación
  if (showValidation) {
    return (
      <BulkUploadValidation
        data={processedData}
        errors={validationErrors}
        onConfirm={handleConfirmUpload}
        onCancel={handleCancelUpload}
        loading={uploadLoading}
      />
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-blue-100 p-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>

      <div className="space-y-4">
        {/* Botón para descargar plantilla */}
        <div className="flex justify-center">
          <button
            onClick={onDownloadTemplate}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md flex items-center space-x-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>Descargar Plantilla</span>
          </button>
        </div>

        {/* Área de carga */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:border-blue-400"
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="space-y-4">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div>
              <p className="text-lg font-medium text-gray-900">
                {selectedFile ? selectedFile.name : "Arrastra tu archivo aquí"}
              </p>
              <p className="text-sm text-gray-500">
                o haz clic para seleccionar un archivo Excel (.xlsx, .xls)
              </p>
            </div>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md inline-block"
            >
              Seleccionar Archivo
            </label>
          </div>
        </div>

        {/* Información adicional */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">Instrucciones:</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Descarga la plantilla y llénala con tus datos</li>
            <li>
              • El archivo debe tener las columnas: Año, Unidad, Tema, Objetivos
              de Aprendizaje, Explicación, Ejemplo, Resultado, Dificultad,
              Conceptos Previos, Tiene Imagen
            </li>
            <li>
              • Los datos se procesarán automáticamente al cargar el archivo
            </li>
            <li>
              • Se crearán las entidades que no existan y se vincularán
              correctamente
            </li>
            <li>
              • Para "Dificultad" usa: números del 1-5 o conceptos como "Básica"
              (1), "Media" (3), "Difícil" (4), etc.
            </li>
            <li>• Para "Tiene Imagen" usa: Si/No, True/False, Yes/No, o 1/0</li>
          </ul>
        </div>

        {/* Estado de carga */}
        {loading && (
          <div className="flex items-center justify-center space-x-2 text-blue-600">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            <span>Procesando archivo...</span>
          </div>
        )}
      </div>
    </div>
  );
};
